// src/components/legal/PodatkiFinansowanie.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, TrendingDown, Shield, Briefcase, FileSearch, HelpCircle, CheckCircle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import Trust from './Trust';
import ContactForm from './ContactForm';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const PodatkiFinansowanie = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const services = [
    {
      icon: <Calculator className="w-8 h-8" />,
      title: "Optymalizacja podatkowa",
      description: "Pomoc w legalnym obniżaniu obciążeń podatkowych (CIT, VAT, PIT) poprzez zastosowanie dostępnych ulg, zwolnień i restrukturyzacji."
    },
    {
      icon: <TrendingDown className="w-8 h-8" />,
      title: "Doradztwo w zakresie ulg i zwolnień podatkowych",
      description: "Identyfikacja i aplikowanie o ulgi B+R, ulgi na innowacje, ulgi inwestycyjne, ulgi w Specjalnych Strefach Ekonomicznych / Polskiej Strefie Inwestycji."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Reprezentacja w sporach podatkowych",
      description: "Skuteczna obrona interesów klientów przed organami skarbowymi i sądami administracyjnymi."
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Finansowanie projektów",
      description: "Doradztwo prawne w zakresie pozyskiwania finansowania (kredyty, leasing, emisja obligacji, private equity) – w tym w kontekście wspomnianych w Szynkowski Consulting dofinansowań i pożyczek."
    },
    {
      icon: <FileSearch className="w-8 h-8" />,
      title: "Due diligence podatkowy",
      description: "Analiza ryzyka podatkowego przy transakcjach fuzji i przejęć."
    },
    {
      icon: <HelpCircle className="w-8 h-8" />,
      title: "Bieżące doradztwo podatkowe",
      description: "Odpowiedzi na pytania i rozwiązywanie problemów podatkowych w codziennej działalności firmy."
    }
  ];

  const benefits = [
    "Połączenie wiedzy prawnej i finansowej",
    "Doświadczenie w złożonych sprawach podatkowych",
    "Proaktywne podejście do optymalizacji",
    "Skuteczność w reprezentacji przed organami"
  ];

  const faqs = [
    {
      question: "Jakie są najpopularniejsze ulgi podatkowe dla firm w Polsce?",
      answer: "W Polsce popularne są ulgi B+R (badawczo-rozwojowa), ulga na robotyzację, ulga na ekspansję, ulga na prototyp, czy ulgi związane z Polską Strefą Inwestycji. Ich zastosowanie zależy od profilu działalności i planowanych inwestycji."
    },
    {
      question: "Kiedy warto skorzystać z doradztwa podatkowego?",
      answer: "Zawsze, gdy planujesz większe inwestycje, restrukturyzację firmy, masz wątpliwości co do interpretacji przepisów, otrzymałeś kontrolę podatkową lub szukasz legalnych sposobów na optymalizację obciążeń podatkowych."
    },
    {
      question: "Czy pomagacie w sporach z Urzędem Skarbowym?",
      answer: "Tak, reprezentujemy klientów we wszystkich etapach postępowań podatkowych i kontrolnych, a także przed sądami administracyjnymi, broniąc ich interesów w sporach z organami skarbowymi."
    }
  ];

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <Calculator className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <TrendingDown className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Podatki i Finansowanie Przedsiębiorstw
            </h1>
            <p className="text-2xl lg:text-3xl text-violet-500 mb-8 font-semibold">
              Optymalizacja i Bezpieczeństwo Prawne
            </p>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Efektywne zarządzanie finansami i minimalizacja obciążeń podatkowych to klucz do sukcesu każdej firmy.
            </p>
            <p className="text-lg text-gray-400 mb-12 max-w-4xl mx-auto">
              Szynkowski Legal oferuje kompleksowe doradztwo prawne w zakresie podatków i finansowania, pomagając Ci zoptymalizować koszty i zabezpieczyć interesy Twojego biznesu.
            </p>
            
            <div className="flex justify-center">
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-6 md:mb-8 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Kompleksowe doradztwo prawne w zakresie podatków i finansowania
            </h2>
            <p className="text-lg text-gray-300">
              Zmieniające się przepisy podatkowe i złożone struktury finansowania wymagają eksperckiej wiedzy. Nasz zespół prawników specjalizuje się w prawie podatkowym i gospodarczym, oferując skuteczne rozwiązania, które pomogą Twojej firmie rozwijać się w bezpieczny i optymalny sposób.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Zakres naszych usług
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="text-violet-600">
                    {service.icon}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Nasze atuty
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <div className="flex justify-center">
                <button 
                  onClick={handleContactScroll}
                  className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase"
                >
                  <span>Bezpłatna konsultacja</span>
                  <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <Trust />

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-black text-white mb-12 text-center uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            FAQ: Podatki i Finansowanie
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between group"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  )}
                </button>
                
                <div className={`transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                  <div className="px-6 pb-4">
                    <p className="text-gray-400">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default PodatkiFinansowanie;